/*
 * Copyright (c) 2026 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import { SbBlokData, StoryblokServerComponent } from '@storyblok/react/rsc'
import React from 'react'
import { StoryblokManualImage } from '@/components/dashboard/manuals/ui/ManualImage'
import { ExpandableManualContent } from '@/components/storyblok/components/manuals/ExpandableManualContent'
import { StoryblokTypographyManuals } from '@/components/storyblok/components/ui/Typography-manuals'

/**
 * The blok names the manual surface resolves differently from the canonical component map. Exported separately from
 * the components (as plain strings) so tests can assert the override set without evaluating the component modules.
 */
const manualOverrideBlokNames = ['typography_copy', 'image', 'expandable-content', 'resource'] as const

/**
 * Resolve a blok name to its manual-surface component.
 *
 * Returns a component to override the canonical map, `null` to deliberately render nothing, or `undefined` to fall
 * through to the canonical map. Implemented as a lazy switch — NOT a module-scope object literal — because
 * `ExpandableManualContent` imports `ManualBlok` back from this module, and dereferencing the import bindings at
 * module-evaluation time would throw under some load orders.
 *
 * If editors start nesting swapped bloks inside other container bloks (`flow-content`, `grid`, `image-text-block`,
 * ...) in manual content, extend this override set (and the containers' child rendering) — never add a second
 * `storyblokInit` call, its registry is first-call-wins per JS realm.
 */
const getManualOverride = (name: string | undefined): React.ElementType | null | undefined => {
  switch (name) {
    case 'typography_copy':
      return StoryblokTypographyManuals
    case 'image':
      return StoryblokManualImage
    case 'expandable-content':
      return ExpandableManualContent
    case 'resource':
      // Resources render in the manual resource sidebar, never inline. Mapping them to nothing here keeps that
      // behaviour without the "Component resource doesn't exist" console noise from the library registry.
      return null
    default:
      return undefined
  }
}

/** A description of the available properties for this component. */
type ManualBlokProps = {
  /** The Storyblok blok to resolve and render. */
  blok: SbBlokData
} & Record<string, unknown>

/**
 * Resolves bloks for the kit-manual surface. The manual-specific swaps are direct static imports — no registry, no
 * per-realm state — so the same blok deterministically renders the same component in RSC, SSR, and the browser,
 * regardless of which routes were visited first. Everything else falls through to the canonical map registered by
 * `@/lib/storyblok/initialisation`.
 */
const ManualBlok: React.FC<ManualBlokProps> = ({ blok, ...rest }) => {
  const Override = getManualOverride(blok.component)
  if (Override === null) return null
  if (Override) return <Override blok={blok} {...rest} />

  return <StoryblokServerComponent blok={blok} {...rest} />
}

export { ManualBlok, manualOverrideBlokNames }
